/**
 * VoiceCloneForm Component
 *
 * Form for cloning a voice from audio samples.
 * Includes name, description, and audio file upload.
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mic } from 'lucide-react';
import { AudioFileUpload } from './AudioFileUpload';
import type { IVoiceCloneFormProps, IVoiceCloneFormInput } from '@/@types/voices';
import { VOICE_CONSTANTS } from '@/@types/voices';

function VoiceCloneForm({ onSubmit, isSubmitting, onCancel }: IVoiceCloneFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audioFiles, setAudioFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      // Validate name
      const trimmedName = name.trim();
      if (!trimmedName) {
        setError('Voice name is required');
        return;
      }
      if (trimmedName.length > VOICE_CONSTANTS.MAX_NAME_LENGTH) {
        setError(`Name must be ${VOICE_CONSTANTS.MAX_NAME_LENGTH} characters or less`);
        return;
      }

      // Validate description
      if (description.length > VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
        setError(
          `Description must be ${VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters or less`
        );
        return;
      }

      // Validate audio files
      if (audioFiles.length < VOICE_CONSTANTS.MIN_AUDIO_FILES) {
        setError(`At least ${VOICE_CONSTANTS.MIN_AUDIO_FILES} audio file is required`);
        return;
      }

      const formInput: IVoiceCloneFormInput = {
        name: trimmedName,
        description: description.trim() || undefined,
        audioFiles,
      };

      await onSubmit(formInput);
    },
    [name, description, audioFiles, onSubmit]
  );

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
    setError(null);
  }, []);

  const handleFilesChange = useCallback((files: File[]) => {
    setAudioFiles(files);
    setError(null);
  }, []);

  const canSubmit = name.trim() && audioFiles.length >= VOICE_CONSTANTS.MIN_AUDIO_FILES;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
          <Mic className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Clone Your Voice
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Upload audio samples to create a cloned voice
          </p>
        </div>
      </div>

      {/* Name Field */}
      <div className="space-y-2">
        <Label htmlFor="voice-name">
          Voice Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="voice-name"
          value={name}
          onChange={handleNameChange}
          placeholder="e.g., My Voice, Narrator Voice"
          maxLength={VOICE_CONSTANTS.MAX_NAME_LENGTH}
          disabled={isSubmitting}
        />
        <p className="text-xs text-gray-500">
          {name.length}/{VOICE_CONSTANTS.MAX_NAME_LENGTH} characters
        </p>
      </div>

      {/* Description Field */}
      <div className="space-y-2">
        <Label htmlFor="voice-description">Description</Label>
        <Textarea
          id="voice-description"
          value={description}
          onChange={handleDescriptionChange}
          placeholder="Optional description (e.g., Calm narrator for tutorials)"
          maxLength={VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH}
          disabled={isSubmitting}
          rows={2}
        />
        <p className="text-xs text-gray-500">
          {description.length}/{VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters
        </p>
      </div>

      {/* Audio Files Upload */}
      <div className="space-y-2">
        <Label>
          Audio Samples <span className="text-red-500">*</span>
        </Label>
        <AudioFileUpload
          files={audioFiles}
          onFilesChange={handleFilesChange}
          maxFiles={VOICE_CONSTANTS.MAX_AUDIO_FILES}
          maxFileSizeMB={VOICE_CONSTANTS.MAX_AUDIO_FILE_SIZE_MB}
          disabled={isSubmitting}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 rounded-md">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting || !canSubmit}>
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Cloning Voice...
            </>
          ) : (
            <>
              <Mic className="w-4 h-4 mr-2" />
              Clone Voice
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export { VoiceCloneForm };
